import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Loader from '../../component/layout/Loader'
import { toast } from 'react-toastify'
import IconCaretsDown from '../../component/icon/IconCaretsDown'
import AnimateHeight from 'react-animate-height'
import Dropdown from '../../component/Dropdown'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-date-picker'
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import api from '../../api/api'
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";


const HomeworkStudent = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const params = useParams()
    const [loading, setLoading] = useState(false)
    const [submission, setSubmission] = useState<any>(null)
    const [error, setError] = useState<string | null>(null)
    // Rating submit state
    const [submittingRating, setSubmittingRating] = useState<boolean>(false)
    // File modal state
    const [selectedFile, setSelectedFile] = useState<any>(null)
    const [showFileModal, setShowFileModal] = useState(false)

    // Get URL parameters and route params
    const homeworkId = params.homeworkId
    const studentId = params.studentId

    // Fetch the student's submission details when component mounts or params change
    useEffect(() => {
        const fetchSubmission = async () => {
            if (!studentId || !homeworkId) return
            setLoading(true)
            setError(null)
            try {
                const res = await api.get(`${process.env.REACT_APP_API_BASE_URL}/homework/student/${studentId}/details/${homeworkId}/`)
                // API returns { code, message, data }
                const submissionData = res.data.data
                setSubmission(submissionData)

                // Check if we need to mark submission as seen
                if (submissionData?.tracking?.is_submission_seen === false) {
                    try {
                        const markSeenPayload = {
                            homework_id: parseInt(homeworkId!),
                            student_id: parseInt(studentId!)
                        }
                        await api.post(`${process.env.REACT_APP_API_BASE_URL}/homework/coach/mark-submission-seen/`, markSeenPayload)
                        console.log('Submission marked as seen by coach')
                    } catch (markSeenErr) {
                        console.error('Error marking submission as seen:', markSeenErr)
                        // Don't show error to user as this is background functionality
                    }
                }
            } catch (err: any) {
                console.error('Error fetching student submission:', err)
                setError('Failed to load submission')
                toast.error('Failed to load submission')
            } finally {
                setLoading(false)
            }
        }

        fetchSubmission()
    }, [studentId, homeworkId])

    // Handle file view in modal
    const viewFile = (file: any) => {
        setSelectedFile(file)
        setShowFileModal(true)
    }

    const closeFileModal = () => {
        setSelectedFile(null)
        setShowFileModal(false)
    }

    return (
        <div>
            {loading && <Loader/>}
            <ul className="flex space-x-2 rtl:space-x-reverse">
                <li>
                    <button 
                        onClick={() => navigate('/homework')}
                        className="text-primary hover:underline cursor-pointer"
                    >
                        Homework
                    </button>
                </li>
                <li className="before:content-['/'] ltr:before:mr-2 rtl:before:ml-2">
                    <button onClick={() => navigate(`/homework/${homeworkId}`)} className="text-primary hover:underline cursor-pointer" >
                        Details
                    </button>
                </li>
                <li className="before:content-['/'] ltr:before:mr-2 rtl:before:ml-2">
                    <span>Student</span>
                </li>
            </ul>
            
            {/* Homework Info Card */}
            {submission && (
                <div className="mt-6 max-w-full mx-auto">
                    <div className="bg-white border-l-4 border-orange-400 rounded-lg p-4 shadow-sm">
                        <div className="space-y-2">
                            <h3 className="text-lg font-semibold text-black">
                                {submission.homework?.title || 'Homework Title'}
                            </h3>
                            <div className="text-gray-700">
                                <p>{submission.homework?.description || 'Homework description'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="pt-5">
                {/* Student Info Section */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                        <img
                            src={submission?.student_pic || '/assets/dummy/default-profile.jpg'}
                            alt={submission?.student_name || 'Student'}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-lg font-semibold">{submission?.student_name}</h2>
                        <p className="text-gray-600 text-sm">
                            Submission: {submission?.submitted_at ? new Date(submission.submitted_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '12 Dec 2024, 14:00pm'}
                        </p>
                    </div>
                </div>

                {/* Content Section */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-semibold">Content</h3>
                        <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-2 mb-4 border border-gray-200">
                        {loading && <div className="text-sm text-gray-500">Loading submission...</div>}
                        {error && <div className="text-sm text-red-500">{error}</div>}
                        {!loading && submission && (
                            <div className="space-y-1">
                                <div className="text-gray-800">
                                    {submission.notes || 'No description provided'}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Files Section */}
                    {!loading && submission && Array.isArray(submission.files) && submission.files.length > 0 && (
                        <div className="space-y-3">
                            {submission.files.map((file: any, idx: number) => (
                                <div key={file.id || idx} className="flex items-center space-x-3 p-2 bg-white rounded-lg border">
                                    <span className="text-sm font-medium flex-1 truncate">{file.file_name || `File ${idx + 1}`}</span>
                                    <button
                                        type="button"
                                        onClick={() => viewFile(file)}
                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                    >
                                        View
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Teacher rating block (Formik) */}
                <div className="mt-6">
                    <h3 className="font-semibold mb-2">Teacher's Comment</h3>
                    <Formik
                        enableReinitialize
                        initialValues={{
                            comments: submission?.grade?.comments  ?? '',
                            rate: submission?.grade?.rate ?? 0,
                            marks: submission?.grade?.marks ?? 0,
                        }}
                        onSubmit={async (values) => {
                            if (submittingRating) return
                            setSubmittingRating(true)
                            try {
                                const submissionId = submission?.id ?? submission?.submission_id ?? 0
                                const payload = {
                                    submission_id: submissionId,
                                    marks: values.marks,
                                    rate: values.rate,
                                    comments: values.comments,
                                }
                                const res = await api.post(`${process.env.REACT_APP_API_BASE_URL}/homework/rate/`, payload)
                                toast.success('Rating submitted')
                                // If API returns updated rating data, merge it into submission
                                if (res.data && res.data.data) {
                                    setSubmission((prev: any) => ({ ...prev, ...res.data.data }))
                                }
                                // Navigate back to homework detail page
                                navigate(`/homework/${homeworkId}`)
                            } catch (err) {
                                console.error('Error submitting rating', err)
                                toast.error('Failed to submit rating')
                            } finally {
                                setSubmittingRating(false)
                            }
                        }}
                    >
                        {({ values, setFieldValue, handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                                <div className="rounded-lg  bg-white">
                                    <textarea
                                        name="comments"
                                        value={values.comments}
                                        onChange={(e) => setFieldValue('comments', e.target.value)}
                                        placeholder="Write your comment here..."
                                        className="w-full min-h-[120px] p-3 resize-vertical border rounded focus:outline-none"
                                        disabled={submittingRating || submission?.grade}
                                    />
                                </div>

                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="border rounded-lg p-4 text-center">
                                        <div className="mb-2 font-semibold">Stars</div>
                                        <div className="flex items-center justify-center space-x-1">
                                            {[1, 2, 3, 4, 5].map((i) => (
                                                <button key={i} type="button" onClick={() => setFieldValue('rate', i)} className="p-1" aria-label={`Rate ${i}`}>
                                                    <svg width="28" height="28" viewBox="0 0 24 24" fill={i <= values.rate ? '#f59e0b' : 'none'} stroke="#f59e0b" strokeWidth="1.5">
                                                        <path d="M12 .587l3.668 7.431L23.4 9.75l-5.7 5.545L18.836 24 12 20.202 5.164 24l1.136-8.705L.6 9.75l7.732-1.732z"/>
                                                    </svg>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="border rounded-lg p-4 flex items-center justify-between">
                                        <div>
                                            <div className="font-semibold">Score</div>
                                        </div>
                                        <div className="text-right">
                                            <input
                                                name="marks"
                                                type="number"
                                                min={0}
                                                max={100}
                                                value={values.marks}
                                                onChange={(e) => setFieldValue('marks', Number(e.target.value))}
                                                className="w-20 text-center text-2xl font-bold border rounded px-2 py-1"
                                            />
                                            <div className="text-sm text-gray-600">/100</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <button 
                                        type="submit" 
                                        className={`w-full btn btn-primary ${(submittingRating || submission?.grade) ? 'opacity-60 cursor-not-allowed' : ''}`} 
                                        disabled={submittingRating || submission?.grade}
                                    >
                                        {submittingRating ? 'Submitting...' : submission?.grade ? 'Rated' : 'Submit Rating'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            </div>

            {/* File View Modal */}
            {showFileModal && selectedFile && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-lg font-semibold">{selectedFile.file_name}</h3>
                            <button
                                onClick={closeFileModal}
                                className="text-gray-500 hover:text-gray-700 text-xl"
                            >
                                ×
                            </button>
                        </div>
                        <div className="flex-1 p-4 overflow-auto">
                            {selectedFile.file_name?.toLowerCase().includes('.pdf') ? (
                                <iframe
                                    src={selectedFile.presigned_url}
                                    className="w-full h-96"
                                    title="PDF Viewer"
                                />
                            ) : (
                                <img
                                    src={selectedFile.presigned_url}
                                    alt={selectedFile.file_name}
                                    className="max-w-full h-auto mx-auto"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement
                                        target.style.display = 'none'
                                        target.parentElement!.innerHTML = `
                                            <div class="text-center text-red-500 p-8">
                                                <p>Unable to load file: ${selectedFile.file_name}</p>
                                                <p class="text-sm text-gray-500 mt-2">The file may be corrupted or the link has expired.</p>
                                            </div>
                                        `
                                    }}
                                />
                            )}
                        </div>
                        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                            <span className="text-sm text-gray-500">
                                Size: {(selectedFile.file_size / 1024).toFixed(1)} KB
                            </span>
                            <div className="flex gap-2">
                                {/* <a
                                    href={selectedFile.file_url}
                                    download={selectedFile.file_name}
                                    className="btn btn-secondary btn-sm"
                                >
                                    Download
                                </a> */}
                                <button
                                    onClick={closeFileModal}
                                    className="btn btn-primary btn-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}

export default HomeworkStudent